import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { MapPin, Store, Cpu, Navigation, Clock } from "lucide-react";
import { useSystemSettings } from "../../lib/systemSettings";

const TYPE_LABEL = { automat: "Automat", sb_shop: "SB-Shop" };
const TYPE_ICON = { automat: Cpu, sb_shop: Store };

/**
 * Baut den bestmöglichen „Navigiere zu"-Link:
 *  1. Wenn Geokoordinaten vorhanden → Google-Maps-Directions mit Koordinaten
 *     (funktioniert plattformübergreifend; iOS/Android bieten selbst den
 *     Öffnen-mit-Karten-App-Dialog an).
 *  2. Sonst freie Adress-Query aus Straße/Hausnr./PLZ/Stadt.
 * Google-Maps' Universal-URL wird gewählt statt `geo:` / `maps://`, damit
 * das Verhalten auf allen Geräten (Desktop + Mobile) gleich funktioniert.
 */
function buildNavigationUrl(l) {
  if (l.latitude != null && l.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      `${l.latitude},${l.longitude}`
    )}`;
  }
  const parts = [
    [l.street, l.house_no].filter(Boolean).join(" "),
    [l.zip, l.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const q = parts || l.title || "";
  return q
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`
    : null;
}

export default function PortalLocations() {
  const [zip, setZip] = useState("");
  const [assortmentLoc, setAssortmentLoc] = useState(null);
  const { brandName } = useSystemSettings();
  const q = useQuery({
    queryKey: ["portal-locations", zip],
    queryFn: async () => (await api.get("/portal/locations", { params: { zip: zip || undefined } })).data,
  });
  const list = q.data || [];
  return (
    <div className="space-y-4" data-testid="portal-locations">
      <div>
        <div className="text-3xl font-black uppercase tracking-tight break-words">{brandName} Standort Finder</div>
        <div className="text-sm text-zinc-500">Finde einen {brandName}-Standort in deiner Nähe.</div>
      </div>
      <div className="max-w-xs">
        <Label className="text-xs uppercase tracking-wider text-zinc-400">PLZ</Label>
        <Input
          data-testid="portal-locations-zip"
          placeholder="z. B. 35745"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
          className="mt-1 bg-zinc-900 border-zinc-800"
        />
      </div>
      {q.isLoading && <div className="text-zinc-500">Lade…</div>}
      {!q.isLoading && list.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500">
          Keine Standorte gefunden{zip ? ` für PLZ „${zip}"` : ""}.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {list.map((l) => {
          const Icon = TYPE_ICON[l.type] || MapPin;
          const navUrl = buildNavigationUrl(l);
          return (
            <div
              key={l.id}
              data-testid={`portal-loc-${l.id}`}
              className="border border-zinc-800 rounded-lg p-4 bg-zinc-900 flex flex-col"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-2">
                <Icon size={14} />
                <span>{TYPE_LABEL[l.type] || l.type}</span>
              </div>
              <div className="font-black text-lg tracking-tight">{l.title}</div>
              {(l.street || l.house_no || l.zip || l.city) && (
                <div className="text-sm text-zinc-400 mt-1">
                  {[l.street, l.house_no].filter(Boolean).join(" ")}
                  {(l.zip || l.city) ? <><br />{[l.zip, l.city].filter(Boolean).join(" ")}</> : null}
                </div>
              )}
              {l.image_url && (
                <div
                  data-testid={`portal-loc-${l.id}-image`}
                  className="mt-3 rounded-md overflow-hidden border border-zinc-800 bg-zinc-950 aspect-video"
                >
                  <img
                    src={l.image_url}
                    alt={l.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  data-testid={`portal-loc-${l.id}-assortment-btn`}
                  onClick={() => setAssortmentLoc(l)}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm bg-cyan-400 text-black hover:bg-cyan-300 uppercase tracking-widest text-[11px] font-bold shadow-sm"
                >
                  Live Sortiment
                </button>
              </div>
              {l.opening_hours && (
                <div
                  data-testid={`portal-loc-${l.id}-hours`}
                  className="mt-3 text-sm text-zinc-300 flex items-start gap-2"
                >
                  <Clock size={14} className="text-cyan-400 mt-[3px] shrink-0" />
                  <div className="whitespace-pre-wrap leading-relaxed">{l.opening_hours}</div>
                </div>
              )}
              {l.description && (
                <div className="text-sm text-zinc-300 mt-3">{l.description}</div>
              )}
              <div className="mt-4 pt-3 border-t border-zinc-800/60 flex">
                <Button
                  asChild
                  disabled={!navUrl}
                  data-testid={`portal-loc-${l.id}-navigate`}
                  className="bg-cyan-400 hover:bg-cyan-300 h-9"
                >
                  <a
                    href={navUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!navUrl}
                    onClick={(e) => { if (!navUrl) e.preventDefault(); }}
                  >
                    <Navigation size={14} className="mr-2 shrink-0" /> Navigiere zu
                  </a>
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <PortalAssortmentDialog loc={assortmentLoc} onClose={() => setAssortmentLoc(null)} />
    </div>
  );
}

function PortalAssortmentDialog({ loc, onClose }) {
  const enabled = !!loc?.id;
  const list = useQuery({
    queryKey: ["portal-assortment", loc?.id],
    queryFn: async () => (await api.get(`/portal/locations/${loc.id}/assortment`)).data,
    enabled,
  });
  if (!enabled) return null;
  const rows = list.data || [];
  const showStock = !!loc.show_stock;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 p-3"
      data-testid="portal-assortment-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400">Aktuelles Sortiment</div>
            <div className="font-black text-lg tracking-tight truncate">{loc.title}</div>
            {!showStock && (
              <div className="text-[11px] text-zinc-500 mt-1">
                Bestandsanzeige durch den Betreiber deaktiviert.
              </div>
            )}
          </div>
          <button
            aria-label="Schließen"
            data-testid="portal-assortment-close"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-200 h-8 px-2 rounded-sm"
          >
            ✕
          </button>
        </div>
        <div className="p-3 overflow-y-auto">
          {list.isLoading && <div className="text-zinc-500 text-sm p-4">Lade Sortiment…</div>}
          {!list.isLoading && rows.length === 0 && (
            <div className="text-zinc-500 text-sm p-6 text-center border border-dashed border-zinc-800 rounded-sm">
              Für diesen Standort ist noch kein Sortiment gepflegt.
            </div>
          )}
          <div className="space-y-2" data-testid="portal-assortment-list">
            {rows.map((r) => {
              const soldOut = showStock && (r.stock ?? 0) <= 0;
              return (
                <div
                  key={r.id}
                  data-testid={`portal-assortment-row-${r.id}`}
                  className="flex items-center gap-3 border border-zinc-800 rounded-sm px-3 py-2 bg-card"
                >
                  {r.product_image_url && (
                    <img src={r.product_image_url} alt={r.product_name}
                      className="w-10 h-10 rounded-sm object-cover border border-zinc-800 shrink-0" loading="lazy" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.product_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {[r.product_brand, r.product_category].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  {showStock ? (
                    <div
                      data-testid={`portal-assortment-stock-${r.id}`}
                      className={`text-xs font-mono uppercase tracking-widest shrink-0 ${soldOut ? "text-rose-400" : "text-cyan-300"}`}
                    >
                      {soldOut ? "Aktuell nicht verfügbar" : `${r.stock} Stück`}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
